import { Users, Calendar, Clock, DollarSign, Percent } from 'lucide-react';

export interface PricingType {
  value: string;
  label: string;
  icon: any;
  description: string;
}

/**
 * Get available pricing types based on option type and core status
 */
export const getAvailablePricingTypes = (optionType: string, isCore: boolean): PricingType[] => {
  const coreTypes: PricingType[] = [
    { value: 'per_person', label: 'Per Person', icon: Users, description: 'Multiply by number of people' },
    { value: 'per_day', label: 'Per Day', icon: Calendar, description: 'Multiply by number of days' },
    { value: 'per_person_day', label: 'Per Person Per Day', icon: Clock, description: 'Multiply by people × days' },
    { value: 'fixed', label: 'Fixed Amount', icon: DollarSign, description: 'Same amount every time' },
    { value: 'percentage', label: 'Percentage', icon: Percent, description: 'Percentage of base price' }
  ];

  const additionalTypes: PricingType[] = [
    { value: 'fixed', label: 'Fixed Amount', icon: DollarSign, description: 'Same amount every time' },
    { value: 'per_person', label: 'Per Person', icon: Users, description: 'Multiply by number of people' },
    { value: 'per_day', label: 'Per Day', icon: Calendar, description: 'Multiply by number of days' },
    { value: 'per_person_day', label: 'Per Person Per Day', icon: Clock, description: 'Multiply by people × days' },
    { value: 'percentage', label: 'Percentage', icon: Percent, description: 'Percentage of base price' }
  ];

  if (isCore) return coreTypes;
  
  switch (optionType) {
    case 'checkbox':
      return additionalTypes.filter(type => ['fixed', 'per_person', 'per_day', 'per_person_day'].includes(type.value));
    case 'select':
      return additionalTypes;
    case 'text':
      return additionalTypes.filter(type => ['fixed', 'percentage'].includes(type.value));
    default:
      return additionalTypes;
  }
};

/**
 * Get currency symbol from currency code
 */
export const getCurrencySymbol = (currency: string): string => {
  return currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
};

/**
 * Format pricing preview text
 */
export const formatPricingPreview = (pricing: any, currency: string): string | null => {
  if (!pricing.value) return null;
  
  const symbol = getCurrencySymbol(currency);
  
  switch (pricing.type) {
    case 'fixed':
      return `+${symbol}${pricing.value}`;
    case 'percentage':
      return `+${pricing.value}%`;
    case 'per_person':
      return `+${symbol}${pricing.value}/person`;
    case 'per_day':
      return `+${symbol}${pricing.value}/day`;
    case 'per_person_day':
      return `+${symbol}${pricing.value}/person/day`;
    default:
      return `+${symbol}${pricing.value}`;
  }
};

/**
 * Validate pricing configuration
 */
export const validatePricingConfig = (pricing: any): { isValid: boolean; error?: string } => {
  if (!pricing.type) {
    return { isValid: false, error: 'Please select a pricing type' };
  }
  
  if (!pricing.value || pricing.value <= 0) {
    return { isValid: false, error: 'Please enter a valid amount greater than 0' };
  }
  
  if (pricing.type === 'percentage' && pricing.value > 100) {
    return { isValid: false, error: 'Percentage cannot exceed 100%' };
  }
  
  return { isValid: true };
};
